import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Megaphone } from 'lucide-react';
import { type TurnstileInstance } from '@marsidev/react-turnstile';
import type { MarketingCategory } from '@evi/shared';
import { TurnstileGate } from '@/components/ui/TurnstileGate';
import {
  lookupEmailPreferences, requestEmailPreferencesLink, updateEmailPreferences, type EmailPreferencesState,
} from '@/lib/api';
import { httpStatusOf } from '@/lib/refreshSession';
import {
  linkWithoutAction, looksLikeEmail, preferenceLinkKey, readPreferenceLink, stageAfterLookup,
} from '@/lib/emailPreferencesFlow';
import { MarketingEmailControls } from './MarketingEmailControls';
import settingsStyles from './SettingsPage.module.css';
import styles from './EmailPreferencesPage.module.css';

/**
 * /email-preferences#t=<token>[&action=unsubscribe]: the page every marketing
 * email's footer links to (23 October 2026). Public, like /join and
 * /home-invite: no sign-in, the signed token in the link is the credential,
 * and it opens one recipient's marketing preferences and nothing else.
 *
 * The token rides in the URL FRAGMENT, so it never reaches a server or CDN
 * log. Opening the page only READS (lib/emailPreferencesFlow): mail scanners
 * render links in a headless browser and run this JavaScript, so nothing may
 * change as the page opens. The footer's Unsubscribe link (action=unsubscribe)
 * shows one prominent "Unsubscribe from all marketing email" button first;
 * the reader's tap sends the unsubscribe, so one tap from the email still
 * unsubscribes. Then the three categories show so a driver can keep the ones
 * they want. A mail provider's own one-click button (List-Unsubscribe-Post)
 * goes straight to the Api and does not pass through here.
 *
 * A lead (a website subscriber with no account) has one switch: off.
 *
 * Only the Api's own answer that the link is bad (404, or 400 for a token it
 * cannot read) shows "This link does not work". A network error, the rate
 * limit or a server error says the request did not go through (for an
 * unsubscribe: that the reader is still subscribed) and offers Try again.
 * The invalid view is never a dead end (23 October 2026, review): the reader
 * types their address and a fresh link is emailed to it, no sign-in, which
 * is a lead's only way to unsubscribe once a link stops working.
 *
 * The page belongs to ONE link: a second link opened in the same tab changes
 * only the fragment, so the route below remounts the page whenever the link
 * changes (preferenceLinkKey), and nothing shown or sent is the previous
 * recipient's.
 */

type View =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'failed'; unsubscribe: boolean }
  | { kind: 'confirm'; state: EmailPreferencesState }
  | { kind: 'ready'; state: EmailPreferencesState };

/** The Api said the link itself is bad, as opposed to the request failing. */
function isInvalidLinkError(err: unknown): boolean {
  const status = httpStatusOf(err);
  return status === 404 || status === 400;
}

interface Props { onNavigate: (path: string) => void }

/** The route: one page per link, remounted when the link in the fragment changes. */
export function EmailPreferencesPage({ onNavigate }: Props) {
  const [linkKey, setLinkKey] = useState(() => preferenceLinkKey(window.location.hash));
  useEffect(() => {
    const onChange = () => setLinkKey(preferenceLinkKey(window.location.hash));
    window.addEventListener('hashchange', onChange);
    window.addEventListener('popstate', onChange);
    return () => {
      window.removeEventListener('hashchange', onChange);
      window.removeEventListener('popstate', onChange);
    };
  }, []);
  return <EmailPreferencesView key={linkKey} onNavigate={onNavigate} />;
}

/** "Email me a new link": the invalid view's no-sign-in way out. Behind
 *  Turnstile, fail-closed like the Api: no widget, no request. */
function RequestLinkForm() {
  const { t } = useTranslation('emailPreferences');
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!looksLikeEmail(email) || state === 'sending' || !turnstileToken) return;
    setState('sending');
    requestEmailPreferencesLink(email.trim(), turnstileToken)
      .then(() => setState('sent'))
      .catch(() => {
        // A token is good for one check: a new one is needed for the retry.
        turnstileRef.current?.reset();
        setTurnstileToken(null);
        setState('error');
      });
  };
  if (state === 'sent') return <p className={styles.intro} role="status">{t('request.sent')}</p>;
  return (
    <form className={styles.requestForm} onSubmit={submit} noValidate>
      <p className={styles.intro}>{t('request.intro')}</p>
      <label className={styles.fieldLabel} htmlFor="email-preferences-request">{t('request.label')}</label>
      <input
        id="email-preferences-request"
        className={styles.input}
        type="email"
        autoComplete="email"
        inputMode="email"
        value={email}
        onChange={(e) => { setEmail(e.target.value); if (state === 'error') setState('idle'); }}
      />
      <TurnstileGate
        ref={turnstileRef}
        posture="closed"
        onToken={setTurnstileToken}
        fallback={<p className={styles.intro} role="alert">{t('request.captchaUnavailable')}</p>}
      />
      <button
        type="submit"
        className={styles.primary}
        disabled={!looksLikeEmail(email) || state === 'sending' || !turnstileToken}
      >
        {state === 'sending' ? t('request.sending') : t('request.submit')}
      </button>
      {state === 'error' && <p className={styles.intro} role="alert">{t('request.failed')}</p>}
    </form>
  );
}

function EmailPreferencesView({ onNavigate }: Props) {
  const { t } = useTranslation('emailPreferences');
  const [{ token, unsubscribeRequested }] = useState(() => readPreferenceLink(window.location.hash));
  const [view, setView] = useState<View>(() => (token ? { kind: 'loading' } : { kind: 'invalid' }));
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const started = useRef(false);

  const dropAction = useCallback(() => {
    if (unsubscribeRequested) window.history.replaceState(null, '', linkWithoutAction(window.location.pathname, token));
  }, [token, unsubscribeRequested]);

  /** The read the page makes as it opens, and its Try again. Never a write. */
  const lookup = useCallback(() => {
    setView({ kind: 'loading' });
    lookupEmailPreferences(token)
      .then((state) => {
        setView(stageAfterLookup(state, unsubscribeRequested) === 'confirm_unsubscribe'
          ? { kind: 'confirm', state }
          : { kind: 'ready', state });
      })
      .catch((err: unknown) => setView(isInvalidLinkError(err) ? { kind: 'invalid' } : { kind: 'failed', unsubscribe: false }));
  }, [token, unsubscribeRequested]);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    lookup();
  }, [token, lookup]);

  /** The reader's tap on the one-tap unsubscribe (or its Try again). */
  const unsubscribeFromConfirm = () => {
    setSaving(true);
    setStatus('idle');
    updateEmailPreferences(token, { unsubscribe_all: true })
      .then((state) => { setView({ kind: 'ready', state }); setStatus('saved'); dropAction(); })
      .catch((err: unknown) => setView(isInvalidLinkError(err) ? { kind: 'invalid' } : { kind: 'failed', unsubscribe: true }))
      .finally(() => setSaving(false));
  };

  /** A change on the preferences view: a category switch, or unsubscribe from all. */
  const save = (change: { categories?: Partial<Record<MarketingCategory, boolean>>; unsubscribe_all?: true }) => {
    setSaving(true);
    setStatus('idle');
    updateEmailPreferences(token, change)
      .then((state) => { setView({ kind: 'ready', state }); setStatus('saved'); })
      .catch(() => setStatus('error'))
      .finally(() => setSaving(false));
  };

  return (
    <div className={styles.page}>
      <div className={styles.column}>
        <p className={styles.brand}>EV INITIATIVE</p>
        <h1 className={styles.title}>{t('title')}</h1>

        {view.kind === 'loading' && (
          <p className={styles.intro}><Loader2 size={14} /> {t('loading')}</p>
        )}

        {view.kind === 'invalid' && (
          <>
            <p className={settingsStyles.notifLabel}>{t('invalid.title')}</p>
            <p className={styles.intro}>{t('invalid.desc')}</p>
            <RequestLinkForm />
            <p className={styles.intro}>{t('request.support')}</p>
          </>
        )}

        {view.kind === 'failed' && (
          <>
            <p className={styles.intro} role="alert">{t(view.unsubscribe ? 'failed.unsubscribe' : 'failed.load')}</p>
            <button
              type="button"
              className={styles.primary}
              disabled={saving}
              onClick={() => (view.unsubscribe ? unsubscribeFromConfirm() : lookup())}
            >
              {t('retry')}
            </button>
          </>
        )}

        {view.kind === 'confirm' && (
          <>
            <p className={styles.intro}>{t('confirm.prompt', { email: view.state.email_hint })}</p>
            <button type="button" className={`${styles.primary} ${styles.prominent}`} disabled={saving} onClick={unsubscribeFromConfirm}>
              {saving ? t('unsubscribing') : t('unsubscribeAll')}
            </button>
            <p className={styles.intro}>{t('confirm.note')}</p>
            {view.state.kind === 'driver' && (
              <button
                type="button"
                className={styles.textButton}
                disabled={saving}
                onClick={() => { dropAction(); setView({ kind: 'ready', state: view.state }); }}
              >
                {t('confirm.manage')}
              </button>
            )}
          </>
        )}

        {view.kind === 'ready' && view.state.kind === 'driver' && (
          <>
            <p className={styles.intro}>{t('intro', { email: view.state.email_hint })}</p>
            <div className={settingsStyles.section}>
              <h2 className={settingsStyles.sectionTitle}><Megaphone size={14} /> {t('title')}</h2>
              <MarketingEmailControls
                value={{ categories: view.state.categories, unsubscribed: view.state.unsubscribed }}
                disabled={saving}
                onToggle={(category, on) => save({ categories: { [category]: on } })}
                onUnsubscribeAll={() => save({ unsubscribe_all: true })}
              />
            </div>
          </>
        )}

        {view.kind === 'ready' && view.state.kind === 'lead' && (
          view.state.unsubscribed ? (
            <p className={styles.intro}>{t('lead.unsubscribed', { email: view.state.email_hint })}</p>
          ) : (
            <>
              <p className={styles.intro}>{t('lead.subscribed', { email: view.state.email_hint })}</p>
              <button type="button" className={styles.primary} disabled={saving} onClick={() => save({ unsubscribe_all: true })}>
                {t('lead.unsubscribe')}
              </button>
            </>
          )
        )}

        <p className={styles.status} aria-live="polite">
          {saving ? t('saving') : status === 'saved' ? t('saved') : status === 'error' ? t('error') : ''}
        </p>

        <a
          className={styles.link}
          href="/"
          onClick={(e) => { e.preventDefault(); onNavigate('/'); }}
        >
          {t('openApp')}
        </a>
      </div>
    </div>
  );
}
